import type { ProjectView } from '@/types/current-project';
import { BUDGETS, DELIVERY_TIMES, PROJECT_TYPES } from '@/types';
import { useProjectBrief } from '@/hooks/useProjectViews';
import { Button } from '@/components/ui';

export function ProjectScopeInfo({ project }: { project: ProjectView }) {
  const query = useProjectBrief(project.projectId);
  const brief = query.data?.brief;
  const classes = {
    h6: 'text-left text-base font-normal text-[--text-dark-secondary] mb-1',
    p: 'text-left text-lg text-[--text-dark-primary] whitespace-pre-wrap',
  };
  const budgetRanges = [
    'Below10000',
    'From10000To50000',
    'From50000To100000',
    'Above100000',
  ];
  const deliveryPreferences = [
    'WithinOneMonth',
    'OneToThreeMonths',
    'ThreeToSixMonths',
    'SpecificDate',
  ];
  const projectTypes = [
    'Other',
    'SmartContract',
    'Frontend',
    'MVP',
    'Audit',
    'MobileApp',
  ];
  return (
    <section className="projDetailSection">
      <h2 className="sectionTitle">Project Scope</h2>
      <div className="sectionContent gap-[--spacing-12]">
        {query.isPending && <p role="status">Loading project preferences…</p>}
        {query.isError && (
          <div role="alert" className="text-left">
            <p>Could not load the saved project brief: {query.error.message}</p>
            <Button variant="secondary" onClick={() => void query.refetch()}>
              Retry brief
            </Button>
          </div>
        )}
        {brief && (
          <div className="grid grid-cols-1 gap-[--spacing-5] sm:grid-cols-2 md:grid-cols-3">
            <div>
              <h6 className={classes.h6}>Indicative budget (USD)</h6>
              <p className={classes.p}>
                {BUDGETS[budgetRanges.indexOf(brief.indicativeBudget.range)]}
              </p>
            </div>
            <div>
              <h6 className={classes.h6}>Delivery time</h6>
              <p className={classes.p}>
                {
                  DELIVERY_TIMES[
                    deliveryPreferences.indexOf(brief.delivery.preference)
                  ]
                }
                {brief.delivery.preference === 'SpecificDate' &&
                  ': ' +
                    new Date(
                      brief.delivery.date + 'T00:00:00'
                    ).toLocaleDateString()}
              </p>
            </div>
            <div>
              <h6 className={classes.h6}>Project Type</h6>
              <p className={classes.p}>
                {PROJECT_TYPES[projectTypes.indexOf(brief.projectType)]}
              </p>
            </div>
          </div>
        )}
        <p className={classes.p}>
          {project.description || 'No description provided.'}
        </p>
        {brief && brief.objectives.length > 0 && (
          <div className="flex flex-col gap-2">
            <h6 className={classes.h6}>Key Objectives</h6>
            <ol className="list-decimal space-y-1.5 pl-6 text-left">
              {brief.objectives.map((objective, i) => (
                <li key={i} className={classes.p}>
                  {objective}
                </li>
              ))}
            </ol>
          </div>
        )}
        {brief && brief.constraints.length > 0 && (
          <div className="flex flex-col gap-2">
            <h6 className={classes.h6}>
              Technical &amp; Functional Constraints
            </h6>
            <ul className="list-disc space-y-1.5 pl-6 text-left">
              {brief.constraints.map((constraint, i) => (
                <li key={i} className={classes.p}>
                  {constraint}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
}
